import { useEffect, useState } from "react";

import { getBulletinById, getUserMeGroups } from "@rubi/api-generated";

import { toBulletinFormFromResource } from "@/components/alerts/add-v2/model/adapters";
import { createEmptyBulletinForm } from "@/components/alerts/add-v2/model/defaults";
import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";
import { toMediaSnapshot } from "@/components/alerts/add-v2/persistence/mediaRepository";
import { IPoliceService } from "@/types";

/**
 * Where a form's initial values come from.
 *
 * This is the whole of phase 8's difference. Everything that produces a bulletin enters the same
 * page, and only the source of the starting document changes — model, adapters, validation, the six
 * sections, the entity editor, media and autosave are shared. **Resist adding a `mode` prop to
 * anything below `BulletinFormPage`**: if a new entry point needs a branch, the branch belongs here.
 *
 * Built so far:
 *
 * | Entry                     | Source                                            |
 * | ------------------------- | ------------------------------------------------- |
 * | Blank                     | `createEmptyBulletinForm()` + the officer's service |
 * | Edit an existing bulletin | `GET /bulletin/{id}`                              |
 *
 * Still to come: from a PDF (AI extraction fills the form before it opens) and from a RubiREPORT
 * occurrence (`?token=`, where the Rust API runs the extraction and returns a one-time token). Both
 * end the same way this one does — a `BulletinFormValues` and, if a row already exists, its id.
 *
 * ## Why this loads *before* the form mounts
 *
 * The plan said `reset()`, on the grounds that `BulletinFormProvider` captures `defaultValues` once
 * and the data arrives late. Loading first is better, and not only simpler: a form mounted on the
 * empty defaults is a form **autosave can fire on**, and the first blur or 800 ms of idle would
 * PATCH a blank document over the bulletin the officer just opened. Not mounting until the values
 * exist makes that impossible rather than unlikely, so `reset()` is still not needed anywhere.
 */

export type BulletinSourceStatus = "loading" | "ready" | "error";

export interface BulletinSource {
  status: BulletinSourceStatus;
  /** The document to start from. Only set once `status` is `"ready"`. */
  values: BulletinFormValues | null;
  /**
   * The row these values came from, so autosave PATCHes it instead of creating a second one, and so
   * the duplicate-occurrence check can stop reporting the bulletin as its own duplicate.
   */
  bulletinId: number | null;
  /**
   * Whether the loaded bulletin is still a draft. `true` for a blank form, which is what it is
   * about to become.
   *
   * The caller has to check it: autosave sends `is_draft: true`, and the endpoint answers
   * `Cannot return state to draft` for a bulletin that is already published — so opening a
   * published one here would fail **every** save. See `BulletinFormPage`.
   */
  isDraft: boolean;
}

/** A route param that `Number()` turned into something no bulletin could be. */
const isUsableId = (id: number): boolean => Number.isInteger(id) && id > 0;

/**
 * `undefined` — a blank form, pre-filled with the officer's own police service as `UpdateAlert`
 * does. Fetched here rather than read from `AuthStateContext`: the context fills its groups
 * asynchronously, and seeding the field after mount would count as an edit and fire an autosave
 * that creates a draft row the officer never asked for.
 */
export const useBulletinSource = (bulletinId?: number): BulletinSource => {
  const [source, setSource] = useState<BulletinSource>(() =>
    bulletinId === undefined || isUsableId(bulletinId)
      ? {
          status: "loading",
          values: null,
          bulletinId: bulletinId ?? null,
          isDraft: true,
        }
      : { status: "error", values: null, bulletinId, isDraft: true },
  );

  useEffect(() => {
    if (bulletinId === undefined) {
      setSource({
        status: "loading",
        values: null,
        bulletinId: null,
        isDraft: true,
      });

      let current = true;

      // A failed lookup only costs the pre-fill, so it still opens the blank form.
      getUserMeGroups()
        .then(({ data }) =>
          data?.items?.find((group) => group.is_police_service),
        )
        .catch(() => undefined)
        .then((service) => {
          if (!current) return;
          setSource({
            status: "ready",
            values: {
              ...createEmptyBulletinForm(),
              // Same cast `usePoliceServiceOptions` makes: `IPoliceService` is a `Pick` of the
              // generated group resource.
              policeService: (service as unknown as IPoliceService) ?? null,
            },
            bulletinId: null,
            isDraft: true,
          });
        });

      return () => {
        current = false;
      };
    }

    // `/rubialert/bulletins/abc/edit` reaches here as `NaN`, which is not `undefined` — so the
    // request went out as `/py/bulletin/NaN`. Error, not `undefined`: treating it as "no id" would
    // open a blank new bulletin at an edit URL, which looks like the record loaded empty.
    if (!isUsableId(bulletinId)) {
      setSource({ status: "error", values: null, bulletinId, isDraft: true });
      return;
    }

    // Set on the way out as well as in the initialiser: the id can change without this component
    // unmounting, and a stale document must not be editable while the new one is in flight.
    setSource({ status: "loading", values: null, bulletinId, isDraft: true });

    let current = true;

    getBulletinById({ path: { bulletin_id: bulletinId } })
      .then(({ data }) => {
        if (!current) return;
        if (!data) {
          setSource({
            status: "error",
            values: null,
            bulletinId,
            isDraft: true,
          });
          return;
        }

        const { media, pdf } = toMediaSnapshot(data);
        setSource({
          status: "ready",
          values: toBulletinFormFromResource(data, media, pdf),
          bulletinId,
          // `is_draft` is an int column, and it is the one field of the resource the form model
          // deliberately has no room for — it belongs to persistence (decision 15).
          isDraft: data.is_draft !== 0,
        });
      })
      .catch(() => {
        if (current) {
          setSource({
            status: "error",
            values: null,
            bulletinId,
            isDraft: true,
          });
        }
      });

    return () => {
      current = false;
    };
  }, [bulletinId]);

  return source;
};
